import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FaUser,
  FaEnvelope,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaSave,
  FaKey,
} from 'react-icons/fa';
import '../Css/Settings.css';
import { useToast } from '../../components/Toast';
import { baseurl } from '../Baseurl';

const getPasswordStrength = (password) => {
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  };

  const passedCount = Object.values(checks).filter(Boolean).length;

  let label = 'Weak';
  let color = '#dc2626';

  if (passedCount === 4) {
    label = 'Strong';
    color = '#16a34a';
  } else if (passedCount === 3) {
    label = 'Medium';
    color = '#f59e0b';
  }

  return { checks, passedCount, label, color };
};

function Settings() {
  const toast = useToast();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      setName(parsed.name || '');
      setEmail(parsed.email || '');
    }
  }, []);

  const passwordStrength = getPasswordStrength(newPassword);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
  });

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);

    try {
      const res = await axios.put(
        `${baseurl}/auth/profile`,
        { name, email },
        getAuthHeaders()
      );

      const updatedUser = res.data.data.user;
      localStorage.setItem('user', JSON.stringify(updatedUser));

      toast(res.data.message || 'Profile updated successfully', 'success');
    } catch (err) {
      console.log('Update profile error:', err.response?.data || err.message);
      toast(err.response?.data?.message || 'Failed to update profile', 'error');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast('New password and confirm password do not match', 'error');
      return;
    }

    if (!passwordStrength.checks.length || !passwordStrength.checks.uppercase ||
        !passwordStrength.checks.lowercase || !passwordStrength.checks.number) {
      toast('New password does not meet the requirements', 'error');
      return;
    }

    setPasswordLoading(true);

    try {
      const res = await axios.put(
        `${baseurl}/auth/change-password`,
        { currentPassword, newPassword },
        getAuthHeaders()
      );

      toast(res.data.message || 'Password changed successfully', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      setTimeout(() => {
        localStorage.clear();
        window.location.href = '/';
      }, 2000);
    } catch (err) {
      console.log('Change password error:', err.response?.data || err.message);
      toast(err.response?.data?.message || 'Failed to change password', 'error');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h2>Account Settings</h2>
        <p>Manage your profile information and account security</p>
      </div>

      <div className="settings-grid">
        {/* PROFILE CARD */}
        <div className="settings-card">
          <div className="settings-card-head">
            <FaUser className="settings-card-icon" />
            <h3>Profile Information</h3>
          </div>

          <form onSubmit={handleProfileSubmit}>
            <label className="field-label">Full Name</label>
            <div className="input-group">
              <FaUser className="input-icon" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                required
              />
            </div>

            <label className="field-label">Email Address</label>
            <div className="input-group">
              <FaEnvelope className="input-icon" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@salon.com"
                required
              />
            </div>

            <p className="settings-hint">
              Changing your email will require you to verify it again before you can log in with it.
            </p>

            <button type="submit" className="settings-btn" disabled={profileLoading}>
              {profileLoading ? (
                <>
                  <span className="btn-spinner"></span> Saving...
                </>
              ) : (
                <>
                  <FaSave /> Save Changes
                </>
              )}
            </button>
          </form>
        </div>

        {/* PASSWORD CARD */}
        <div className="settings-card">
          <div className="settings-card-head">
            <FaKey className="settings-card-icon" />
            <h3>Change Password</h3>
          </div>

          <form onSubmit={handlePasswordSubmit}>
            <label className="field-label">Current Password</label>
            <div className="input-group">
              <FaLock className="input-icon" />
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                required
              />
              <span className="toggle-icon" onClick={() => setShowCurrent(!showCurrent)}>
                {showCurrent ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            <label className="field-label">New Password</label>
            <div className="input-group">
              <FaLock className="input-icon" />
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
              />
              <span className="toggle-icon" onClick={() => setShowNew(!showNew)}>
                {showNew ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            {newPassword && (
              <div className="password-strength">
                <div className="strength-bar-track">
                  <div
                    className="strength-bar-fill"
                    style={{
                      width: `${(passwordStrength.passedCount / 4) * 100}%`,
                      background: passwordStrength.color,
                    }}
                  ></div>
                </div>
                <span className="strength-label" style={{ color: passwordStrength.color }}>
                  {passwordStrength.label}
                </span>
              </div>
            )}

            <label className="field-label">Confirm New Password</label>
            <div className="input-group">
              <FaLock className="input-icon" />
              <input
                type={showNew ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                required
              />
            </div>

            <p className="settings-hint">
              You will be logged out on all devices after changing your password.
            </p>

            <button type="submit" className="settings-btn" disabled={passwordLoading}>
              {passwordLoading ? (
                <>
                  <span className="btn-spinner"></span> Updating...
                </>
              ) : (
                <>
                  <FaKey /> Change Password
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Settings;